import api, { ApiError } from './api';

const BASE = '/driver-applications';

const wrap = async (promise) => {
  try {
    return await promise;
  } catch (error) {
    throw error instanceof ApiError ? error : ApiError.fromResponse(error);
  }
};

const driverApplicationService = {
  // ---- public / applicant ------------------------------------------------
  async getInvite(token) {
    const data = await wrap(api.get(`${BASE}/invite/${encodeURIComponent(token)}`));
    return data.invite;
  },

  async acceptInvite(token) {
    return wrap(api.post(`${BASE}/invite/${encodeURIComponent(token)}/accept`, {}));
  },

  /** @returns {{ application, company, legal }} */
  async getMine() {
    return wrap(api.get(`${BASE}/me`));
  },

  /** @returns {{ application, warnings, draftIssues }} */
  async saveStep(applicationId, step, data, mode = 'draft') {
    return wrap(api.patch(`${BASE}/${applicationId}/steps/${step}`, data, { params: { mode } }));
  },

  /** @returns {{ document, documents }} */
  async uploadDocument(applicationId, slot, file, onProgress) {
    const formData = new FormData();
    formData.append('document', file, file.name);
    formData.append('slot', slot);
    return wrap(api.upload(`${BASE}/${applicationId}/documents`, formData, {
      timeout: 120000,
      onUploadProgress: (event) => {
        if (typeof onProgress === 'function' && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      }
    }));
  },

  async removeDocument(applicationId, documentId) {
    return wrap(api.delete(`${BASE}/${applicationId}/documents/${documentId}`));
  },

  async getDocumentUrl(applicationId, documentId) {
    return wrap(api.get(`${BASE}/${applicationId}/documents/${documentId}/url`));
  },

  async submit(applicationId, payload) {
    return wrap(api.post(`${BASE}/${applicationId}/submit`, payload, { timeout: 120000 }));
  },

  async getPdfUrl(applicationId, version = 'submitted') {
    return wrap(api.get(`${BASE}/${applicationId}/pdf`, { params: { version } }));
  },

  // ---- manager -----------------------------------------------------------
  async createInvite(payload) {
    return wrap(api.post(BASE, payload));
  },

  /** @returns {{ applications, total }} */
  async list(params = {}) {
    return wrap(api.get(BASE, { params }));
  },

  async stats() {
    const data = await wrap(api.get(`${BASE}/stats`));
    return data.stats;
  },

  /** @returns {{ application, company, legal }} */
  async get(applicationId) {
    return wrap(api.get(`${BASE}/${applicationId}`));
  },

  async resendInvite(applicationId) {
    return wrap(api.post(`${BASE}/${applicationId}/resend-invite`, {}));
  },

  async revokeInvite(applicationId) {
    return wrap(api.post(`${BASE}/${applicationId}/revoke-invite`, {}));
  },

  async requestChanges(applicationId, payload) {
    return wrap(api.post(`${BASE}/${applicationId}/request-changes`, payload));
  },

  async reject(applicationId, payload) {
    return wrap(api.post(`${BASE}/${applicationId}/reject`, payload));
  },

  async approve(applicationId, payload) {
    return wrap(api.post(`${BASE}/${applicationId}/approve`, payload, { timeout: 120000 }));
  },

  async hire(applicationId, payload) {
    return wrap(api.post(`${BASE}/${applicationId}/hire`, payload, { timeout: 120000 }));
  },

  async notifications() {
    return wrap(api.get(`${BASE}/notifications`));
  },

  async markNotificationsRead(ids = null) {
    return wrap(api.post(`${BASE}/notifications/read`, ids ? { ids } : {}));
  },

  async regeneratePdf(applicationId, version) {
    return wrap(api.post(`${BASE}/${applicationId}/regenerate-pdf`, { version }, { timeout: 120000 }));
  }
};

/** Map server `details: [{field, message}]` onto react-hook-form setError for one step. */
export const applyServerFieldErrors = (error, setError, { prefixToStrip = '' } = {}) => {
  const details = Array.isArray(error?.details) ? error.details : [];
  let applied = 0;
  details.forEach((detail) => {
    if (!detail?.field) return;
    const name = prefixToStrip && detail.field.startsWith(`${prefixToStrip}.`)
      ? detail.field.slice(prefixToStrip.length + 1)
      : detail.field;
    try {
      setError(name, { type: 'server', message: detail.message });
      applied += 1;
    } catch (err) {
      // ignore unknown field names
    }
  });
  return applied;
};

// The driver-application API writes its 403/404/409/410 messages for end users
// ("Only the sections flagged by the reviewer can be changed."); the generic
// ApiError mapping would replace them with "You do not have permission…".
const SPECIFIC_MESSAGE_STATUSES = [403, 404, 409, 410];

export const errorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback;
  if (SPECIFIC_MESSAGE_STATUSES.includes(error.status) && error.message && error.code && error.code !== 'UNKNOWN_ERROR') {
    return error.message;
  }
  if (typeof error.getUserFriendlyMessage === 'function') {
    const friendly = error.getUserFriendlyMessage();
    if (friendly) return friendly;
  }
  return error.message || fallback;
};

export default driverApplicationService;
